import axios, { type AxiosRequestConfig } from 'axios';
import ApiClient, { getXSRFToken } from './ApiClient';

let apiClient: ApiClient;

const getApiClient = () => {
  if (!apiClient) {
    apiClient = new ApiClient();
  }
  return apiClient;
};

export async function fetchCsrfCookie(): Promise<void> {
  if (import.meta.env.DEV) {
    const response = await fetch('/api/sanctum/csrf-cookie', {
      method: 'GET',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      }
    });

    if (!response.ok) {
      console.error(
        'CSRF request failed:',
        response.status,
        response.statusText
      );
      throw new Error(`CSRF request failed: ${response.status}`);
    }

    return;
  }

  await getApiClient().get('/sanctum/csrf-cookie');
}

export function getStatefulApiHeaders(
  overrides: Record<string, string> = {}
): Record<string, string> {
  const token = localStorage.getItem('token');
  const headers: Record<string, string> = {
    Accept: 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...overrides
  };

  const xsrfToken = getXSRFToken();
  if (xsrfToken) {
    headers['X-XSRF-TOKEN'] = xsrfToken;
  }

  return headers;
}

export function getStatefulAxiosConfig(
  headerOverrides: Record<string, string> = {}
): AxiosRequestConfig {
  return {
    headers: getStatefulApiHeaders(headerOverrides),
    withCredentials: true
  };
}

export async function statefulGet<T = unknown>(
  url: string,
  config?: AxiosRequestConfig
) {
  return axios.get<T>(url, {
    ...getStatefulAxiosConfig(),
    ...config,
    headers: {
      ...getStatefulAxiosConfig().headers,
      ...config?.headers
    }
  });
}

export async function statefulPost<T = unknown>(
  url: string,
  data?: unknown,
  headerOverrides: Record<string, string> = {}
) {
  await fetchCsrfCookie();

  return axios.post<T>(url, data, {
    ...getStatefulAxiosConfig({
      'Content-Type': 'application/json',
      ...headerOverrides
    })
  });
}

export async function statefulPut<T = unknown>(
  url: string,
  data?: unknown,
  headerOverrides: Record<string, string> = {}
) {
  await fetchCsrfCookie();

  return axios.put<T>(url, data, {
    ...getStatefulAxiosConfig({
      'Content-Type': 'application/json',
      ...headerOverrides
    })
  });
}
